import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Image,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Endpoints } from "../../Lib/endpoints";

import { useDisclosure } from "@mantine/hooks";

import {
  IconAlertCircle,
  IconBook,
  IconCheck,
  IconChevronRight,
  IconClock,
  IconCreditCard,
  IconMail,
  IconTicket,
  IconRefresh,
  IconStar,
  IconUsers,
} from "@tabler/icons-react";

import { useTranslation } from "react-i18next";

import { Colors, HttpSuccessCodesObj } from "../../Lib/data";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getCurrencySymbol,
  getEllipsisFromString,
} from "../../Lib/methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/responses";
import {
  CourseProps,
  PlanCouponValidationResponseProps,
  PlanProps,
  StudentProps,
} from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";

import { motion } from "framer-motion";
import { AllFatherContext } from "../../App";
import MegaLoader from "../../Components/MegaLoader";
import { AppContext } from "../Dashboard";
import {
  getPreferredPaymentCurrency,
  isBrazilPaymentCountry,
} from "../../Lib/payment";
import "./styles.scss";
// Note: Make sure to set REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file
// Example: REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_your_stripe_key_here
const PurchasePlan: React.FC = () => {
  const { t } = useTranslation();

  const context = useContext(AppContext);
  const allFatherContext = useContext(AllFatherContext);
  const { planID } = useParams();
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState<CourseProps | null>(null);
  const [isCourseModalOpen, setIsCourseModalOpen] = useState(false);
  const [resendVerificationLoading, setResendVerificationLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponPricing, setCouponPricing] =
    useState<PlanCouponValidationResponseProps | null>(null);
  const activeCurrency = getPreferredPaymentCurrency(
    allFatherContext?.locationInformation?.currency
  );

  const { data: plan, isLoading: isLoadingPlan } = usePerformRequest<
    NonPaginatedResponse<PlanProps>
  >({
    method: Endpoints.GetSinglePlanDetails(planID as string, activeCurrency).method,
    route: Endpoints.GetSinglePlanDetails(planID as string, activeCurrency).route,
  });

  const {
    data: courses,
    isLoading: isLoadingCourses,
    reloadData: reloadCourses,
  } = usePerformRequest<PaginatedResponse<CourseProps[]>>({
    method: Endpoints.GetCourses(1, 30).method,
    route: Endpoints.GetCourses(1, 30).route,
    body: planID ? { planID } : {},
    showError: false,
  });

  useEffect(() => {
    if (planID) {
      reloadCourses();
    }
  }, [planID]);

  const [isPaymentLoading, { open: StartPaymentLoading, close: StopPaymentLoading }] =
    useDisclosure(false);

  const {
    data: studentProfile,
    isLoading: isLoadingProfile,
  } = usePerformRequest<NonPaginatedResponse<StudentProps>>({
    method: Endpoints.GetStudentProfile().method,
    route: Endpoints.GetStudentProfile().route,
  });

  const ResendVerificationEmail = async () => {
    setResendVerificationLoading(true);
    const r = await PerformRequest({
      method: Endpoints.GetResendVerificationEmail().method,
      route: Endpoints.GetResendVerificationEmail().route,
      data: {
        email: context?.profile?.email,
      },
      callback: () => {
        setResendVerificationLoading(false);
      },
    });
    setResendVerificationLoading(false);
    if (r) {
      if (r.status === HttpSuccessCodesObj.Success) {
        DefaultSuccessNotification("Verification email sent successfully!");
      } else {
        DefaultErrorNotification(r.message ?? "Failed to send verification email");
      }
    }
  };

  const handlePaymentError = (error: string) => {
    DefaultErrorNotification(error || t("plans.paymentError"));
  };

  // Check if student is already enrolled in this plan
  const studentPlans: string[] = studentProfile?.data?.enrolledPlans ?? [];
  const isAlreadyEnrolled = studentPlans.includes(planID!);

  const handlePurchaseClick = async () => {
    if (!studentProfile?.data) {
      DefaultErrorNotification("Please log in to purchase a plan");
      return;
    }

    // Check if student is already enrolled in this plan
    if (isAlreadyEnrolled) {
      DefaultErrorNotification("You are already enrolled in this plan!");
      return;
    }

    if (!planID) {
      DefaultErrorNotification("Plan not found");
      return;
    }

    StartPaymentLoading();

    const r = await PerformRequest<
      NonPaginatedResponse<{
        url: string;
        sessionId: string;
      }>
    >({
      method: Endpoints.GetCreateStripeSubscription().method,
      route: Endpoints.GetCreateStripeSubscription().route,
      data: {
        planID,
        currency: activeCurrency,
        couponCode:
          couponPricing?.coupon?.code ??
          (couponCode.trim() ? couponCode.trim() : undefined),
      },
      callback: () => {
        StopPaymentLoading();
      },
    });

    StopPaymentLoading();

    if (r?.status === HttpSuccessCodesObj.Created && r.data?.data?.url) {
      window.location.assign(r.data.data.url);
      return;
    }

    handlePaymentError(r?.message ?? "Failed to start Stripe checkout");
  };

  if (isLoadingPlan || isLoadingProfile) {
    return (
      <Container size="md" py="xl">
        <MegaLoader />
      </Container>
    );
  }

  if (!plan?.data) {
    return (
      <Container size="md" py="xl">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Text>{t("plans.title")}</Text>
        </div>
      </Container>
    );
  }

  const displayedPrice = couponPricing?.finalPrice ?? plan.data.price;

  const ApplyCoupon = async () => {
    if (!planID || !couponCode.trim()) {
      DefaultErrorNotification("Enter a coupon code first");
      return;
    }

    setIsApplyingCoupon(true);
    const r = await PerformRequest<NonPaginatedResponse<PlanCouponValidationResponseProps>>({
      method: Endpoints.GetValidatePlanCoupon(planID).method,
      route: Endpoints.GetValidatePlanCoupon(planID).route,
      data: {
        currency: activeCurrency,
        couponCode,
      },
      callback: () => {
        setIsApplyingCoupon(false);
      },
    });
    setIsApplyingCoupon(false);

    if (r?.status === HttpSuccessCodesObj.Success && r.data?.data) {
      setCouponPricing(r.data.data);
      DefaultSuccessNotification("Coupon applied successfully");
      return;
    }

    setCouponPricing(null);
    DefaultErrorNotification(r?.message ?? "Coupon code is invalid");
  };

  const RemoveCoupon = () => {
    setCouponPricing(null);
    setCouponCode("");
  };

  return (
    <>
      <div className="purchase-plan-container">
            <Container size="md" py="xl">
              {/* Email Verification Alert */}
              {context?.profile && !context.profile.isEmailVerified && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  style={{ marginBottom: "2rem" }}
                >
                  <Paper
                    shadow="md"
                    withBorder
                    p="xl"
                    radius="lg"
                    className="email-verification-alert"
                  >
                    <Stack gap="lg">
                      <Group gap="md" align="center">
                        <div className="verification-icon">
                          <IconMail size={24} />
                        </div>
                        <div>
                          <Title order={3} className="verification-title">
                            {t("purchase.emailVerificationRequired.title")}
                          </Title>
                          <Text size="sm" c="dimmed">
                            {t("purchase.emailVerificationRequired.prompt")}
                          </Text>
                        </div>
                      </Group>

                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="orange"
                        variant="light"
                        title={t("purchase.emailVerificationRequired.verificationRequired")}
                      >
                        <Text size="sm" mb="md">
                          Your email address <strong>{context.profile.email}</strong> needs to be
                          verified before you can make a purchase. Please check your inbox and click
                          the verification link.
                        </Text>

                        <Group gap="sm">
                          <Button
                            size="sm"
                            variant="filled"
                            color="orange"
                            loading={resendVerificationLoading}
                            onClick={ResendVerificationEmail}
                            leftSection={<IconRefresh size={14} />}
                          >
                            {t("purchase.emailVerificationRequired.resend")}
                          </Button>
                          <Text size="xs" c="dimmed">
                            {t("purchase.emailVerificationRequired.spamHint")}
                          </Text>
                        </Group>
                      </Alert>

                      <Group gap="sm" justify="center">
                        <Text size="sm" c="dimmed">
                          {t("purchase.emailVerificationRequired.alreadyVerified")}
                        </Text>
                        <Button
                          variant="subtle"
                          size="sm"
                          loading={context?.isLoadingProfile}
                          onClick={() => context?.reloadProfile()}
                          leftSection={<IconRefresh size={14} />}
                        >
                          {t("purchase.emailVerificationRequired.refreshPage")}
                        </Button>
                      </Group>
                    </Stack>
                  </Paper>
                </motion.div>
              )}

              {/* Enrollment Status Alert */}
              {isAlreadyEnrolled && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  style={{ marginBottom: "2rem" }}
                >
                  <Paper
                    shadow="md"
                    withBorder
                    p="xl"
                    radius="lg"
                    className="enrollment-status-alert"
                  >
                    <Stack gap="lg">
                      <Group gap="md" align="center">
                        <div className="enrollment-icon">
                          <IconCheck size={24} color={Colors.GreenPrimary} />
                        </div>
                        <div>
                          <Title order={3} className="enrollment-title">
                            {t("purchase.alreadyEnrolled.title")}
                          </Title>
                          <Text size="sm" c="dimmed">
                            {t("purchase.alreadyEnrolled.desc")}
                          </Text>
                        </div>
                      </Group>

                      <Alert
                        icon={<IconCheck size={16} />}
                        color="green"
                        variant="light"
                        title={t("purchase.alreadyEnrolled.activeSubscription")}
                      >
                        <Text size="sm" mb="md">
                          You have an active subscription to <strong>{plan.data.name}</strong>. You
                          can access all courses in this plan from your dashboard.
                        </Text>

                        <Group gap="sm">
                          <Button
                            size="sm"
                            variant="filled"
                            color="green"
                            onClick={() => navigate("/d/courses")}
                            leftSection={<IconBook size={14} />}
                          >
                            {t("purchase.alreadyEnrolled.viewMyCourses")}
                          </Button>
                          <Text size="xs" c="dimmed">
                            {t("purchase.alreadyEnrolled.accessHint")}
                          </Text>
                        </Group>
                      </Alert>
                    </Stack>
                  </Paper>
                </motion.div>
              )}

              <Paper shadow="xs" withBorder p="xl" radius="md">
                <Stack gap="xl">
                  <Stack gap="md">
                    <div className="flex-row align-center">
                      <IconChevronRight color={Colors.PurplePrimary} />
                      <Text fw={600} size="xl" c={Colors.PurplePrimary}>
                        {plan.data.name}
                      </Text>
                    </div>
                    <Text c="dimmed">{plan.data.description}</Text>

                    <Group justify="space-between" mt="xl">
                      <div>
                        <Text fw={700} fz={30} c={Colors.GreenPrimary}>
                          {getCurrencySymbol(activeCurrency)}
                          {displayedPrice.toLocaleString()}
                          <span
                            style={{
                              fontSize: "1rem",
                              color: "var(--mantine-color-dimmed)",
                            }}
                          >
                            {t("plans.monthly")}
                          </span>
                        </Text>
                        <Text size="sm" c="dimmed" mt={4}>
                          {t("plans.subscriptionInfo")}
                        </Text>
                        {isBrazilPaymentCountry && (
                          <Text size="sm" c={Colors.BluePrimary} mt={4}>
                            Brazil checkout supports Stripe card and PIX.
                          </Text>
                        )}
                        {couponPricing?.coupon && (
                          <Text size="sm" c={Colors.GreenPrimary} mt={4}>
                            Coupon {couponPricing.coupon.code} applied. You save{" "}
                            {getCurrencySymbol(activeCurrency)}
                            {couponPricing.coupon.discountAmount.toLocaleString()}.
                          </Text>
                        )}
                        <Group gap="xs" mt={8}>
                          <Badge
                            color="blue"
                            variant="light"
                            size="sm"
                            leftSection={<IconCheck size={12} />}
                          >
                            {t("plans.autoRenewal")}
                          </Badge>
                          <Badge
                            color="green"
                            variant="light"
                            size="sm"
                            leftSection={<IconCheck size={12} />}
                          >
                            {t("plans.cancelAnytime")}
                          </Badge>
                        </Group>
                      </div>

                      <Button
                        size="md"
                        color={isAlreadyEnrolled ? "gray" : Colors.PurplePrimary}
                        onClick={handlePurchaseClick}
                        c={Colors.White}
                        className="purchase-plan-button"
                        loading={isPaymentLoading}
                        disabled={
                          isPaymentLoading ||
                          (context?.profile && !context.profile.isEmailVerified) ||
                          isAlreadyEnrolled
                        }
                        leftSection={<IconCreditCard size={18} />}
                      >
                        {context?.profile && !context.profile.isEmailVerified
                          ? "Email Verification Required"
                          : isAlreadyEnrolled
                          ? t("plans.alreadyEnrolled")
                          : t("plans.purchase")}
                      </Button>
                    </Group>
                  </Stack>
                  <Divider />
                  <Stack gap="sm">
                    <Text fw={600}>Coupon code</Text>
                    <Group align="flex-end" wrap="nowrap">
                      <TextInput
                        placeholder="Enter coupon code"
                        value={couponCode}
                        onChange={(event) =>
                          setCouponCode(event.currentTarget.value.toUpperCase())
                        }
                        leftSection={<IconTicket size={16} />}
                        style={{ flex: 1 }}
                      />
                      <Button
                        variant="outline"
                        onClick={ApplyCoupon}
                        loading={isApplyingCoupon}
                      >
                        Apply
                      </Button>
                      {couponPricing?.coupon && (
                        <Button variant="subtle" color="red" onClick={RemoveCoupon}>
                          Remove
                        </Button>
                      )}
                    </Group>
                    {couponPricing?.coupon && (
                      <Paper withBorder radius="md" p="md">
                        <Stack gap={6}>
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                              Current price
                            </Text>
                            <Text size="sm">
                              {getCurrencySymbol(activeCurrency)}
                              {couponPricing.earlyBirdAdjustedPrice.toLocaleString()}
                            </Text>
                          </Group>
                          <Group justify="space-between">
                            <Text size="sm" c="dimmed">
                              Coupon discount
                            </Text>
                            <Text size="sm" c={Colors.GreenPrimary}>
                              -{getCurrencySymbol(activeCurrency)}
                              {couponPricing.coupon.discountAmount.toLocaleString()}
                            </Text>
                          </Group>
                          <Group justify="space-between">
                            <Text fw={600}>Total</Text>
                            <Text fw={700} c={Colors.GreenPrimary}>
                              {getCurrencySymbol(activeCurrency)}
                              {couponPricing.finalPrice.toLocaleString()}
                            </Text>
                          </Group>
                        </Stack>
                      </Paper>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            </Container>

            {/* Courses Section */}
            {courses?.data && courses.data.length > 0 && (
              <Container size="lg" py="xl">
                <div className="courses-section">
                  <div className="section-header">
                    <Title order={2} className="section-title">
                      {t("courses.coursesInPlan")}
                    </Title>
                    <Text className="section-subtitle">{t("purchase.coursesInPlanSubtitle")}</Text>
                  </div>

                  {isLoadingCourses ? (
                    <div className="courses-loader">
                      <MegaLoader size={50} />
                    </div>
                  ) : (
                    <SimpleGrid
                      cols={{ base: 1, md: 2, lg: 3 }}
                      spacing="lg"
                      className="courses-grid"
                    >
                      {courses.data.map((course) => (
                        <Card
                          key={course.id}
                          className="course-card"
                          onClick={() => {
                            setSelectedCourse(course);
                            setIsCourseModalOpen(true);
                          }}
                          style={{ cursor: "pointer" }}
                        >
                          <div className="course-image">
                            <Image
                              src={
                                course.thumbnail ||
                                course.images[0] ||
                                "https://placehold.co/600x400"
                              }
                              alt={course.name}
                            />
                          </div>
                          <div className="course-content">
                            <Title order={3} className="course-title">
                              {course.name}
                            </Title>
                            <Text className="course-description">
                              {getEllipsisFromString(course.description, 120)}
                            </Text>
                            <Group gap="sm" className="course-badges">
                              <Badge
                                leftSection={<IconClock size={12} />}
                                color="blue"
                                variant="light"
                                size="sm"
                              >
                                {t("purchase.weeksCount", { count: 8 })}
                              </Badge>
                              <Badge
                                leftSection={<IconUsers size={12} />}
                                color="green"
                                variant="light"
                                size="sm"
                              >
                                {t("purchase.studentsCountText", { value: "1.2k" })}
                              </Badge>
                            </Group>
                          </div>
                        </Card>
                      ))}
                    </SimpleGrid>
                  )}
                </div>
              </Container>
            )}

            {/* Course Details Modal */}
            <Modal
              opened={isCourseModalOpen}
              onClose={() => setIsCourseModalOpen(false)}
              size="lg"
              centered
              classNames={{
                header: "course-modal-header",
                body: "course-modal-body",
                content: "course-modal-content",
              }}
            >
              {selectedCourse && (
                <div className="course-modal">
                  <div className="course-modal-hero">
                    <Image
                      src={
                        selectedCourse.thumbnail ||
                        selectedCourse.images[0] ||
                        "https://placehold.co/800x400"
                      }
                      alt={selectedCourse.name}
                      className="course-modal-image"
                    />
                    <div className="course-modal-overlay">
                      <Title order={1} className="course-modal-title">
                        {selectedCourse.name}
                      </Title>
                      <Group gap="md" className="course-modal-badges">
                        <Badge leftSection={<IconClock size={14} />} color="blue" variant="light">
                          {t("purchase.weeksCount", { count: 8 })}
                        </Badge>
                        <Badge leftSection={<IconUsers size={14} />} color="green" variant="light">
                          {t("purchase.studentsCountText", { value: "1.2k" })}
                        </Badge>
                        <Badge leftSection={<IconStar size={14} />} color="yellow" variant="light">
                          {t("purchase.ratingShort", { value: "4.8" })}
                        </Badge>
                      </Group>
                    </div>
                  </div>

                  <div className="course-modal-content-wrapper">
                    <div className="course-modal-description">
                      <Title order={3} mb="md">
                        {t("purchase.course.about")}
                      </Title>
                      <Text size="lg" lineClamp={0}>
                        {selectedCourse.description}
                      </Text>
                    </div>

                    <Divider my="xl" />

                    <div className="course-modal-features">
                      <Title order={3} mb="md">
                        {t("purchase.course.whatYouWillLearn")}
                      </Title>
                      <Stack gap="md">
                        <Group gap="sm">
                          <IconCheck size={20} color={Colors.PurplePrimary} />
                          <Text>{t("purchase.course.learnBullet1")}</Text>
                        </Group>
                        <Group gap="sm">
                          <IconCheck size={20} color={Colors.PurplePrimary} />
                          <Text>{t("purchase.course.learnBullet2")}</Text>
                        </Group>
                        <Group gap="sm">
                          <IconCheck size={20} color={Colors.PurplePrimary} />
                          <Text>{t("purchase.course.learnBullet3")}</Text>
                        </Group>
                      </Stack>
                    </div>

                    <Divider my="xl" />

                    <div className="course-modal-subscription">
                      <Title order={3} mb="md">
                        {t("purchase.course.subscriptionAccess")}
                      </Title>
                      <Alert
                        icon={<IconCheck size={16} />}
                        color="blue"
                        variant="light"
                        title={t("purchase.course.includedTitle")}
                      >
                        <Text size="sm">{t("purchase.course.includedText")}</Text>
                      </Alert>
                    </div>
                  </div>
                </div>
              )}
            </Modal>

          </div>
    </>
  );
};

export default PurchasePlan;
